import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Handlebars from "handlebars";
import { useDispatch, useSelector } from "react-redux";
import { selectAppointments, selectShops } from "../redux/selectors";
import {
  updateAppointment,
  resetAppointment,
} from "../redux/slices/appointmentSlice";
// import { resetShops } from "./redux/slices/shopSlice";
import { captureBooking } from "../utils/api";
import { sendToWindowDataLayer } from "../utils/dom";
import {
  borderColor,
  shadowColor,
  shadow,
  rounded,
  cardHeaderClass,
  cardBodyClass,
} from "css";

const ValidationSchema = Yup.object().shape({
  fname: Yup.string().required("(required)"),
  lname: Yup.string().required("(required)"),
  phone: Yup.string().required("(required)"),
  comments: Yup.string().required("(required)"),
});

const StayOrGo = ({ appointment }) => {
  const [showThanks, setShowThanks] = useState(false);
  const [bailOutValues, setBailOutValues] = useState({});
  const [bailMessage, setBailMessage] = useState(
    "If you change your mind, we're here to help!"
  );

  const dispatch = useDispatch();

  const hexToRgba = (hex, opacity) => {
    // Remove the hash if present
    hex = hex.replace("#", "");

    // Convert 3-digit hex to 6-digit hex
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((hexChar) => hexChar + hexChar)
        .join("");
    }

    // Parse the r, g, b values from the hex string
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    // Return the rgba color
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  useEffect(() => {
    buildHandlebars();
  }, []);

  const buildHandlebars = () => {
    const template = Handlebars.compile(
      appointment?.shopData?.messages?.bailMessage ??
        "If you change your mind, we're here to help!"
    );

    const data = {
      your_service: appointment?.service,
      on_date: appointment?.date,
      at_time: appointment?.time,
    };
    const result = template(data);
    setBailMessage(result);
  };

  const bailout = (values) => {
    values = {
      customer: {
        fname: values.fname,
        lname: values.lname,
        phone: values.phone,
      },
      bailOutStage: {
        bailOutStep: appointment.previousStage,
        bailOutComments: values.comments,
      },
      shopId: appointment.shopData?.shopId,
      domain: appointment.domain,
      shopData: appointment.shopData,
      date: appointment.date,
      time: appointment.time,
      service: appointment.service,
      bookingId: appointment.bookingId,
      bailed: true,
    };

    captureBooking(values).then((res) => {
      dispatch(
        updateAppointment({
          updates: {
            bailed: true,
          },
        })
      );
      if (appointment?.customer?.phone) {
        sendToWindowDataLayer(
          "dc-service-partial_conversion",
          "dc-service-partial_conversion"
        );
      }
      sendToWindowDataLayer("dc-service-bailed", "dc-service-bailed");
      setBailOutValues(values);
      setShowThanks(true);
    });
  };

  const AreYouSure = () => {
    let message = bailMessage;

    return (
      <div
        dangerouslySetInnerHTML={{ __html: message }}
        className="mb-6 p-6 rounded-xl"
        style={{
          backgroundColor: appointment?.shopData?.color
            ? hexToRgba(appointment.shopData.color, 0.3)
            : "rgba(48, 86, 206, 0.3)", // fallback color
        }}
      ></div>
    );
  };

  return (
    <div className="mx-6 pt-6">
      <div
        className={`${cardHeaderClass} flex justify-between`}
        style={{ backgroundColor: appointment?.shopData?.color }}
      >
        What'd we miss?
      </div>
      <div
        className={cardBodyClass}
        style={{ borderColor: appointment?.shopData?.color }}
      >
        <div className="md:p-6 justify-items-center p-4 rounded-md">
          {showThanks && appointment.stage > 0 ? (
            <div style={{ whiteSpace: "pre-line" }}>
              {appointment?.shopData?.bailOutThanks ?? (
                <>
                  Thanks for visiting,{" "}
                  {bailOutValues?.customer?.fname?.charAt(0).toUpperCase() +
                    bailOutValues?.customer?.fname?.slice(1)}
                  ! We will reach out to you shortly to discuss your service
                  needs. In the meantime, feel free to contact us at{" "}
                  <a
                    href={`tel:${appointment?.shopData?.shopContact?.phone?.number}`}
                  >
                    {appointment?.shopData?.shopContact?.phone?.number}
                  </a>
                  <br />
                  <br />
                  <em>- Your {appointment.shopData?.shopName?.name} Team</em>
                </>
              )}
            </div>
          ) : (
            <Formik
              initialValues={{
                fname: "",
                lname: "",
                phone: "",
                comments: "",
              }}
              validationSchema={ValidationSchema}
              onSubmit={(values) => {
                bailout(values);
              }}
            >
              {(
                { touched, errors, handleSubmit } // Destructure handleSubmit here
              ) => (
                <Form>
                  <AreYouSure />

                  <div className="grid grid-rows-3 md:grid-rows-3 md:grid-cols-2 grid-flow-row md:grid-flow-col gap-4">
                    <div className="flex flex-col">
                      <label htmlFor="fname">
                        First Name{" "}
                        <ErrorMessage
                          name="fname"
                          component="span"
                          className="text-red-500 text-sm"
                        />
                      </label>
                      <Field
                        name="fname"
                        type="text"
                        className={`md:w-56 p-2 border rounded`}
                        style={{
                          borderColor: appointment?.shopData?.color,
                        }}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label htmlFor="lname">
                        Last Name{" "}
                        <ErrorMessage
                          name="lname"
                          component="span"
                          className="text-red-500 text-sm"
                        />
                      </label>
                      <Field
                        name="lname"
                        type="text"
                        className={`md:w-56 p-2 border rounded`}
                        style={{
                          borderColor: appointment?.shopData?.color,
                        }}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label htmlFor="phone">
                        Phone Number{" "}
                        <ErrorMessage
                          name="phone"
                          component="span"
                          className="text-red-500 text-sm"
                        />
                      </label>
                      <Field
                        name="phone"
                        type="text"
                        className={`md:w-56 p-2 border rounded`}
                        style={{
                          borderColor: appointment?.shopData?.color,
                        }}
                      />
                    </div>

                    <div className="row-span-3">
                      <div className="md:col-span-2 flex flex-col">
                        <label htmlFor="comments">
                          How can we help?{" "}
                          <ErrorMessage
                            name="comments"
                            component="span"
                            className="text-red-500 text-sm"
                          />
                        </label>
                        <Field
                          name="comments"
                          as="textarea"
                          rows="4"
                          className={`md:h-[150px] md:w-72 p-2 border rounded`}
                          style={{
                            borderColor: appointment?.shopData?.color,
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="md:grid-cols-2 gap-4 mt-6">
                    <div className="flex align-center md:justify-end">
                      <span
                        className={`py-2 px-2 cursor-pointer border-solid border-[1px] ${borderColor} mr-5 md:mx-0 ${rounded} ${shadow} ${shadowColor}`}
                        style={{
                          border: `1px solid ${appointment?.shopData?.color}`,
                          textAlign: "center",
                        }}
                        onClick={() => {
                          dispatch(
                            updateAppointment({
                              updates: {
                                stage: appointment.previousStage,
                              },
                            })
                          );
                        }}
                      >
                        Continue Booking
                      </span>
                      <span
                        className={`py-2 px-2 cursor-pointer  border-solid border-[1px] ${borderColor} ml-5 md:ml-5 ${rounded} ${shadow} ${shadowColor}`}
                        style={{
                          border: `1px solid ${appointment?.shopData?.color}`,
                          textAlign: "center",
                        }}
                        onClick={handleSubmit} // Now handleSubmit is defined
                      >
                        Request Help
                      </span>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </div>
    </div>
  );
};

export { StayOrGo };
